import { supabase } from "@/integrations/supabase/client";
import { normalizeHref, telegramHref } from "@/lib/social-links";

/** Направления экспертов — в точности как в базе. */
export const EXPERT_CATEGORIES = [
  "Документы и переезд",
  "Право и финансы",
  "Недвижимость",
  "Здоровье",
  "Психология и развитие",
  "Красота",
  "Дети и обучение",
  "Спорт и тело",
  "Творчество и съёмка",
  "Бизнес и маркетинг",
  "Дом и быт",
] as const;

export const MAX_EXPERT_CATEGORIES = 3;

/** Все направления эксперта; первое — главное. */
export function expertCategoriesOf(
  p: { expert_categories?: string[] | null; expert_category?: string | null } | null | undefined,
): string[] {
  const list = (p?.expert_categories ?? []).filter(Boolean);
  if (list.length) return list;
  return p?.expert_category ? [p.expert_category] : [];
}

export const EXPERT_FORMATS: Record<string, string> = {
  online: "Онлайн",
  office: "У себя",
  visit: "С выездом",
};

export const EXPERT_CURRENCIES: Record<string, string> = { TRY: "₺", USD: "$", EUR: "€" };

type ExpertInput =
  | {
      expert_category?: string | null;
      expert_categories?: string[] | null;
      expert_offer?: string | null;
      expert_formats?: string[] | null;
      expert_area?: string | null;
      expert_hidden?: boolean | null;
      telegram?: string | null;
      social_links?: string[] | null;
    }
  | null
  | undefined;

const filled = (v: string | null | undefined) => !!v && v.trim().length > 0;

/** Чего не хватает до роли — зеркало profile_expert_fields_complete + контакт. */
export function expertMissing(p: ExpertInput): string[] {
  const out: string[] = [];
  if (!filled(p?.expert_category)) out.push("указать направление");
  if (!filled(p?.expert_offer)) out.push("описать услугу");
  if ((p?.expert_formats?.length ?? 0) === 0) out.push("указать формат");
  if (!filled(p?.expert_area)) out.push("указать, где работаешь");
  if (!(filled(p?.telegram) || (p?.social_links ?? []).some((s) => filled(s))))
    out.push("добавить контакт");
  return out;
}

/** Зеркало profile_is_expert. */
export function isExpert(p: ExpertInput): boolean {
  return !!p && !p.expert_hidden && expertMissing(p).length === 0;
}

export function formatExpertPrice(
  price: number | null | undefined,
  currency: string | null | undefined,
): string | null {
  if (price === null || price === undefined) return null;
  const sym = EXPERT_CURRENCIES[currency ?? "TRY"] ?? "₺";
  return `от ${new Intl.NumberFormat("ru-RU").format(price)} ${sym}`;
}

export function contactChannel(url: string): "telegram" | "whatsapp" | "instagram" | "link" {
  const u = url.toLowerCase();
  if (u.includes("t.me") || u.includes("telegram")) return "telegram";
  if (u.includes("wa.me") || u.includes("whatsapp")) return "whatsapp";
  if (u.includes("instagram")) return "instagram";
  return "link";
}

/** Фиксируем переход к эксперту без блокировки открытия ссылки. */
export function logExpertContact(expertId: string, channel: string) {
  void supabase.rpc("log_expert_contact", { p_expert: expertId, p_channel: channel }).then(
    () => {},
    () => {},
  );
}

/** Основной контакт человека: сначала Telegram, иначе первая ссылка. */
export function primaryContact(
  p: { telegram?: string | null; social_links?: string[] | null } | null | undefined,
): { href: string; channel: ReturnType<typeof contactChannel> } | null {
  const socials = (p?.social_links ?? []).filter((s) => !!s && s.trim().length > 0);
  const tgLink = telegramHref(p?.telegram);
  const socialTg = socials.find((u) => contactChannel(u) === "telegram");
  const href =
    tgLink ?? (socialTg ? normalizeHref(socialTg) : socials[0] ? normalizeHref(socials[0]) : null);
  return href ? { href, channel: contactChannel(href) } : null;
}

export function bloggerLink(
  p: { is_blogger?: boolean | null; blog_url?: string | null } | null | undefined,
): string | null {
  return p?.is_blogger && p.blog_url?.trim() ? normalizeHref(p.blog_url) : null;
}
